import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { DEMO_MODE_AVAILABLE } from '../config/featureFlags';

/**
 * Demo Mode: when on, the chat answers a fixed script client-side and never
 * calls the backend. Used for external presentations where the presenter is a
 * member of staff (no student record) and the answers must be predictable.
 *
 * Only ever available on Dev builds (DEMO_MODE_AVAILABLE); on every other
 * environment the value is forced to false regardless of localStorage.
 */
const STORAGE_KEY = 'paila-demo-mode';

interface DemoModeContextType {
  demoMode: boolean;
  setDemoMode: (on: boolean) => void;
}

const DemoModeContext = createContext<DemoModeContextType | undefined>(undefined);

function readStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function DemoModeProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useState<boolean>(readStored);

  const setDemoMode = useCallback((on: boolean) => {
    if (!DEMO_MODE_AVAILABLE) return;
    setStored(on);
    try {
      if (on) {
        localStorage.setItem(STORAGE_KEY, '1');
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // localStorage unavailable (private mode etc.) — state still updates for this session
    }
  }, []);

  const value = useMemo(
    () => ({ demoMode: DEMO_MODE_AVAILABLE && stored, setDemoMode }),
    [stored, setDemoMode]
  );

  return <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>;
}

export function useDemoMode() {
  const context = useContext(DemoModeContext);
  if (context === undefined) {
    throw new Error('useDemoMode must be used within a DemoModeProvider');
  }
  return context;
}
